import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { varAlpha } from 'src/theme/styles';
import { displaySerif } from 'src/theme/core/typography';

// ----------------------------------------------------------------------

const TOPICS = ['Games', 'Jackpots', 'Certification', 'RTP'];

// ----------------------------------------------------------------------

// Four motifs layered by role rather than lined up side by side, which would read as three
// stickers: card suits as wallpaper, a roulette wheel cropped by the top-right corner so it sits
// *behind*, the reel window as the one object shown whole, and chips filling the corner the window
// leaves open. Cropping is what gives it depth, and depth is what makes it one picture.
//
// Every colour is a palette token — no literals — so the figure follows the theme instead of
// dating against it. Unlike the discs this replaces, there's no `mix-blend-mode: multiply`: it
// darkens overlaps beautifully on a light panel and crushes the whole figure to near-black on a
// dark one.

// The figure covers its panel rather than sitting inside it (`slice`, below), so the panel's aspect
// ratio decides what gets cropped: a tall panel loses the left and right edges, a wide one the top
// and bottom. Everything that must survive lives inside roughly x 60–340, y 40–360 of this 400×400
// canvas; the lattice, the wheel and the chips are the parts that are meant to run off.
const SAFE_CANVAS = '0 0 400 400';

// A 5×5 lattice, each row shifted one suit along so no two neighbours match. It reaches every edge
// so the pattern fills whatever the crop exposes.
const LATTICE_STEPS = [10, 100, 190, 280, 370];
const SUITS = ['dia', 'spade', 'heart', 'club'];

// Reel columns are centred on 126 / 200 / 274, rows on 172 / 224 / 276. The middle row is three of
// a kind, on the payline — the detail that stops the figure being wallpaper.
const REEL_SYMBOLS = [
  { shape: 'bar', x: 126, y: 172, color: 'primary' },
  { shape: 'cherry', x: 200, y: 172, color: 'error' },
  { shape: 'seven', x: 274, y: 172, color: 'warning' },
  { shape: 'dia', x: 126, y: 224, color: 'primary' },
  { shape: 'dia', x: 200, y: 224, color: 'primary' },
  { shape: 'dia', x: 274, y: 224, color: 'primary' },
  { shape: 'star', x: 126, y: 276, color: 'secondary' },
  { shape: 'bar', x: 200, y: 276, color: 'info' },
  { shape: 'dia', x: 274, y: 276, color: 'success' },
];

const REEL_BANDS = [92, 166, 240];

// The wheel's rim is one dashed ring drawn over another in the second colour — no arc maths to
// maintain. A dash pair is the circumference over the segment count: 2π × 100 / 12 = 52.36.
const RIM_DASH = '52.36 52.36';

const WHEEL = { cx: 352, cy: 62 };

// Spokes on the visible part of the cone only; the rest is off-canvas.
const SPOKES = [
  [374, 62, 402, 62],
  [367.6, 77.6, 387.4, 97.4],
  [352, 84, 352, 112],
  [336.4, 77.6, 316.6, 97.4],
  [330, 62, 302, 62],
];

// Below the window rather than over it, so they can't cover a reel symbol.
const CHIPS = [
  { x: 252, y: 356, color: 'success', shade: 'dark' },
  { x: 198, y: 374, color: 'error', shade: 'main' },
];

function CasinoFigure() {
  const theme = useTheme();
  const { palette } = theme.vars;

  const ink = palette.text.primary;
  const paper = palette.background.paper;

  return (
    <Box
      component="svg"
      viewBox={SAFE_CANVAS}
      // `slice` is SVG's cover: scale until the canvas fills the box, then crop the overflow.
      // `meet` (the default) would letterbox it and leave the grey bands this replaced.
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      sx={{
        inset: 0,
        width: 1,
        height: 1,
        display: 'block',
        position: 'absolute',
        // The wheel is deliberately larger than the canvas — this is what crops it.
        overflow: 'hidden',
      }}
    >
      <defs>
        <linearGradient id="authFigureFadeTop" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={paper} stopOpacity="1" />
          <stop offset="1" stopColor={paper} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="authFigureFadeBottom" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={paper} stopOpacity="0" />
          <stop offset="1" stopColor={paper} stopOpacity="1" />
        </linearGradient>

        {/* Card suits */}
        <path id="authFigureDia" d="M0,-20 L14,0 L0,20 L-14,0 Z" />
        <path
          id="authFigureHeart"
          d="M0,18 C-16,6 -18,-6 -10,-12 C-4,-16 0,-12 0,-8 C0,-12 4,-16 10,-12 C18,-6 16,6 0,18 Z"
        />
        <path
          id="authFigureSpade"
          d="M0,-20 C8,-8 20,-2 20,6 C20,14 12,18 6,14 C6,17 8,19 11,20 H-11 C-8,19 -6,17 -6,14 C-12,18 -20,14 -20,6 C-20,-2 -8,-8 0,-20 Z"
        />
        <g id="authFigureClub">
          <circle cx="0" cy="-9" r="8.5" />
          <circle cx="-9.5" cy="4" r="8.5" />
          <circle cx="9.5" cy="4" r="8.5" />
          <path d="M-8,20 C-4,14 -2,8 0,3 C2,8 4,14 8,20 Z" />
        </g>

        {/* Reel symbols */}
        <g id="authFigureBar">
          <rect x="-22" y="-21" width="44" height="9" rx="2" />
          <rect x="-22" y="-5" width="44" height="9" rx="2" />
          <rect x="-22" y="11" width="44" height="9" rx="2" />
        </g>
        <g id="authFigureCherry">
          <path
            d="M-1,-19 C6,-15 12,-9 13,-2"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M-1,-19 C-6,-13 -10,-8 -11,-2"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx="-11" cy="9" r="11" />
          <circle cx="12" cy="9" r="11" />
        </g>
        <path id="authFigureSeven" d="M-17,-21 H17 V-11 L3,21 H-9 L5,-11 H-17 Z" />
        <path
          id="authFigureStar"
          d="M0,-22 L6.5,-8 L21,-6.8 L10,3 L13,17.6 L0,10.5 L-13,17.6 L-10,3 L-21,-6.8 L-6.5,-8 Z"
        />

        <g id="authFigureChip">
          <circle r="34" fill={paper} stroke={ink} strokeWidth="2" />
          <circle r="34" fill="none" stroke="currentColor" strokeWidth="10" strokeDasharray="17.8 17.8" />
          <circle r="21" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle r="9" fill="currentColor" />
        </g>
      </defs>

      {/* 1 · wallpaper. Any stronger than 0.07 and the reel symbols lose their ground. */}
      <g fill={ink} opacity="0.07">
        {LATTICE_STEPS.map((y, row) =>
          LATTICE_STEPS.map((x, column) => {
            const suit = SUITS[(row + column) % SUITS.length];

            return (
              <use
                key={`${suit}-${x}-${y}`}
                href={`#authFigure${suit.charAt(0).toUpperCase()}${suit.slice(1)}`}
                x={x}
                y={y}
              />
            );
          })
        )}
      </g>

      {/* 2 · the wheel, cropped by the corner */}
      <circle {...WHEEL} r="120" fill="none" stroke={ink} strokeWidth="2" />
      <circle
        {...WHEEL}
        r="100"
        fill="none"
        stroke={ink}
        strokeWidth="36"
        strokeDasharray={RIM_DASH}
      />
      <circle
        {...WHEEL}
        r="100"
        fill="none"
        stroke={palette.error.main}
        strokeWidth="36"
        strokeDasharray={RIM_DASH}
        strokeDashoffset="52.36"
      />
      <circle {...WHEEL} r="82" fill={paper} stroke={ink} strokeWidth="2" />
      <circle {...WHEEL} r="52" fill={palette.primary.main} />
      <g stroke={palette.common.white} strokeWidth="2.5" opacity="0.85">
        {SPOKES.map(([x1, y1, x2, y2]) => (
          <line key={`${x1}-${y1}`} x1={x1} y1={y1} x2={x2} y2={y2} />
        ))}
      </g>
      <circle {...WHEEL} r="19" fill={paper} />
      <circle {...WHEEL} r="6" fill={ink} />
      {/* the ball, resting in a pocket on the visible arc */}
      <circle cx="265" cy="112" r="9" fill={paper} stroke={ink} strokeWidth="2" />

      {/* 3 · the reel window, in front */}
      <rect x="78" y="128" width="244" height="192" rx="18" fill={paper} stroke={ink} strokeWidth="2" />
      {REEL_BANDS.map((x) => (
        <rect key={x} x={x} y="142" width="68" height="164" fill={palette.background.neutral} />
      ))}

      {/* the payline sits under the symbols, so it reads through the gaps rather than across them */}
      <line x1="78" y1="224" x2="322" y2="224" stroke={palette.error.main} strokeWidth="2.5" />
      <polygon points="64,215 76,224 64,233" fill={palette.error.main} />
      <polygon points="336,215 324,224 336,233" fill={palette.error.main} />

      {REEL_SYMBOLS.map((symbol) => (
        <use
          key={`${symbol.shape}-${symbol.x}-${symbol.y}`}
          href={`#authFigure${symbol.shape.charAt(0).toUpperCase()}${symbol.shape.slice(1)}`}
          x={symbol.x}
          y={symbol.y}
          fill={palette[symbol.color].main}
          color={palette[symbol.color].main}
        />
      ))}

      <rect x="92" y="142" width="216" height="26" fill="url(#authFigureFadeTop)" />
      <rect x="92" y="280" width="216" height="26" fill="url(#authFigureFadeBottom)" />
      {/* redrawn over the fades so the window keeps a crisp edge */}
      <rect x="78" y="128" width="244" height="192" rx="18" fill="none" stroke={ink} strokeWidth="2" />

      {/* 4 · chips, balancing the wheel across the diagonal */}
      {CHIPS.map((chip) => (
        <use
          key={`${chip.x}-${chip.y}`}
          href="#authFigureChip"
          x={chip.x}
          y={chip.y}
          color={palette[chip.color][chip.shade]}
        />
      ))}
    </Box>
  );
}

// ----------------------------------------------------------------------

export function Section({
  sx,
  layoutQuery,
  statement = 'One console for game configuration, jackpots, player activity and certification.',
  ...other
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'none',
        [theme.breakpoints.up(layoutQuery)]: {
          display: 'flex',
          flexDirection: 'column',
          // Width as well as basis: the pair holds even if the form column tries to grow past
          // its share.
          flex: '0 0 48%',
          width: '48%',
          maxWidth: 760,
          borderLeft: `1px solid ${theme.vars.palette.divider}`,
        },
        ...sx,
      }}
      {...other}
    >
      {/* The figure fills this box edge to edge, so the box only has to give it an area and a
          backdrop for whatever the pattern doesn't cover. */}
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 320,
          overflow: 'hidden',
          position: 'relative',
          bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
        }}
      >
        <CasinoFigure />
      </Box>

      <Stack
        spacing={3}
        sx={{
          p: 6,
          flexShrink: 0,
          bgcolor: 'background.neutral',
          borderTop: `1px solid ${theme.vars.palette.divider}`,
        }}
      >
        <Typography
          sx={{
            fontFamily: displaySerif,
            fontSize: { md: 26, lg: 30 },
            lineHeight: 1.22,
            letterSpacing: '-0.01em',
          }}
        >
          {statement}
        </Typography>

        <Divider sx={{ borderBottomWidth: 2, borderColor: 'text.primary' }} />

        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {TOPICS.map((topic) => (
            <Typography
              key={topic}
              variant="caption"
              sx={{
                fontWeight: 600,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              {topic}
            </Typography>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}
